import React from 'react';
import { theme, alpha } from '../../../theme';
import { GridBooking, BOOKING_COLORS, CHANNEL_COLORS, BookingChannel } from '../../../types/venue';
import { User } from 'lucide-react';

interface BookingGridCellProps {
  booking?: GridBooking;
  isAvailable: boolean;
  isPast: boolean;
  isCurrentTime?: boolean;
  onClick: (e: React.MouseEvent<HTMLDivElement>) => void;
  height?: number;
}

const BookingGridCell: React.FC<BookingGridCellProps> = ({
  booking,
  isAvailable,
  isPast,
  isCurrentTime = false,
  onClick,
  height = 48,
}) => {
  const getBackgroundColor = () => {
    if (booking) {
      if (booking.status === 'CANCELLED') return BOOKING_COLORS.CANCELLED;
      return booking.color || BOOKING_COLORS[booking.bookingType] || BOOKING_COLORS.ONLINE;
    }
    if (isPast) return theme.colors.background;
    return 'transparent';
  };

  const getTypeLabel = () => {
    if (!booking) return '';
    switch (booking.bookingType) {
      case 'FIXED': return 'FIJO';
      case 'MANUAL': return 'MANUAL';
      case 'ONLINE': return 'ONLINE';
      default: return '';
    }
  };

  const bgColor = getBackgroundColor();

  const cellStyle: React.CSSProperties = {
    height: `${height}px`,
    minHeight: `${height}px`,
    backgroundColor: booking ? 'transparent' : (isPast ? theme.colors.background : 'transparent'),
    border: `1px solid ${theme.colors.border}`,
    borderTop: isCurrentTime ? `2px solid ${theme.colors.danger}` : `1px solid ${theme.colors.border}`,
    cursor: isPast && !booking ? 'default' : 'pointer',
    transition: 'all 0.15s',
    position: 'relative',
    overflow: 'hidden',
    opacity: isPast && !booking ? 0.5 : 1,
    padding: booking ? '0' : '0',
  };

  const channelColor = booking?.bookingType === 'MANUAL' && booking?.bookingChannel
    ? CHANNEL_COLORS[booking.bookingChannel as BookingChannel]
    : undefined;

  const bookingCardStyle: React.CSSProperties = {
    backgroundColor: bgColor,
    borderRadius: '6px',
    margin: '2px 4px',
    padding: '4px 8px',
    height: 'calc(100% - 4px)',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    position: 'relative',
    overflow: 'hidden',
    borderLeft: channelColor ? `3px solid ${channelColor}` : undefined,
  };

  const topRowStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontSize: '10px',
    fontWeight: theme.fonts.weightBold,
    color: 'rgba(0,0,0,0.7)',
    marginBottom: '2px',
  };

  const customerRowStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    fontSize: '12px',
    fontWeight: theme.fonts.weightBold,
    color: '#000',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  };

  const typeBadgeStyle: React.CSSProperties = {
    fontSize: '9px',
    fontWeight: theme.fonts.weightBold,
    color: 'rgba(0,0,0,0.6)',
    backgroundColor: 'rgba(255,255,255,0.3)',
    padding: '1px 5px',
    borderRadius: '3px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  };

  const emptyHoverStyle: React.CSSProperties = {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '18px',
    color: theme.colors.primary,
    opacity: 0,
    transition: 'opacity 0.15s',
  };

  const handleMouseEnter = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isPast && !booking) {
      e.currentTarget.style.backgroundColor = `${alpha('primary', '15')}`;
      const plusIcon = e.currentTarget.querySelector('.plus-icon') as HTMLElement;
      if (plusIcon) plusIcon.style.opacity = '1';
    } else if (booking) {
      const card = e.currentTarget.querySelector('.booking-card') as HTMLElement;
      if (card) card.style.filter = 'brightness(1.1)';
    }
  };

  const handleMouseLeave = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isPast && !booking) {
      e.currentTarget.style.backgroundColor = 'transparent';
      const plusIcon = e.currentTarget.querySelector('.plus-icon') as HTMLElement;
      if (plusIcon) plusIcon.style.opacity = '0';
    } else if (booking) {
      const card = e.currentTarget.querySelector('.booking-card') as HTMLElement;
      if (card) card.style.filter = 'brightness(1)';
    }
  };

  return (
    <div
      data-testid={isAvailable ? 'time-slot' : booking ? 'time-slot-booked' : 'time-slot-past'}
      style={cellStyle}
      onClick={(e) => onClick(e)}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      title={
        booking
          ? `${booking.customerName || 'Sin nombre'} - ${booking.startTime} a ${booking.endTime}`
          : isPast
          ? 'Horario pasado'
          : 'Click para crear reserva'
      }
    >
      {booking ? (
        <div className="booking-card" style={bookingCardStyle}>
          <div style={topRowStyle}>
            <span>{booking.startTime} - {booking.endTime}</span>
            <span style={typeBadgeStyle}>{getTypeLabel()}</span>
          </div>
          <div style={customerRowStyle}>
            <User size={12} />
            <span>{booking.customerName || booking.booker?.name || 'Sin nombre'}</span>
          </div>
        </div>
      ) : (
        !isPast && (
          <div className="plus-icon" style={emptyHoverStyle}>
            +
          </div>
        )
      )}
    </div>
  );
};

export default BookingGridCell;
